import React, { useState, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { X, Brain, Save, Play, Pause, TestTube } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useBackendAITrading } from '../../contexts/BackendAITradingContext';
import { useAIStrategyPersistenceContext } from '../../contexts/AIStrategyPersistenceContext';
import { usePositionContext } from '../../contexts/PositionContext';
import SuperTrendStrategy from './Strategies/SuperTrendStrategy';

interface AITradingSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  currentPrice?: string;
  selectedStrategy: string;
  onStrategyChange: (strategy: string) => void;
  onConfigUpdate: (config: any) => void;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  isRunning?: boolean;
}

interface Strategy {
  id: string;
  name: string;
  description: string;
  component: React.ReactNode;
  isActive: boolean;
}

const AITradingSettings: React.FC<AITradingSettingsProps> = ({
  isOpen,
  onClose,
  symbol,
  currentPrice,
  selectedStrategy,
  onStrategyChange,
  onConfigUpdate,
  onSignalUpdate,
  isRunning
}) => {
  const { theme } = useTheme();
  const { 
    addStrategy, 
    removeStrategy, 
    strategies: backendStrategies, 
    enableAI,
    // New per-asset methods
    getAssetAIConfig,
    getAssetStrategies,
    setAssetAIEnabled,
    stopAssetAITrading, // NEW: Comprehensive stop method
    saveAssetStrategy,
    startAssetAITrading // NEW: Start AI trading method
  } = useBackendAITrading();
  const { saveStrategy, isStrategyActive } = useAIStrategyPersistenceContext();
  const { testThresholdsForSymbol } = usePositionContext(); // 🔥 NEW: Add threshold testing
  
  // 🔥 NEW: Test thresholds function
  const handleTestThresholds = useCallback(() => {
    console.log(`🧪 Testing thresholds for ${symbol}...`);
    testThresholdsForSymbol(symbol);
  }, [symbol, testThresholdsForSymbol]);
  
  // State to track current strategy configuration from user input
  const [currentStrategyConfig, setCurrentStrategyConfig] = useState<any>(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  
  // Handler to capture configuration updates from strategy components
  const handleConfigUpdate = useCallback((config: any) => {
    setCurrentStrategyConfig(config);
    onConfigUpdate?.(config); // Also call the original callback
  }, [onConfigUpdate]);

  // Validation function to check if strategy values are valid
  const validateStrategyConfig = useCallback((config: any, strategyType: string) => {
    const errors: string[] = [];
    
    // Check quantity
    if (!config.quantity || config.quantity <= 0) {
      errors.push('Quantity must be greater than 0');
    }
    
    // Check profit threshold
    if (!config.profitThreshold || config.profitThreshold <= 0) {
      errors.push('Profit Threshold must be greater than 0');
    }
    
    // Check loss threshold
    if (!config.lossThreshold || config.lossThreshold <= 0) {
      errors.push('Loss Threshold must be greater than 0');
    }
    
    // Strategy-specific validations for HUGE♥︎HUG (supertrend/VidyaHeart)
    if (strategyType === 'supertrend') {
      // VidyaHeart Strategy validations
      if (!config.initialEntryLevel || !['1m', '5m', '15m', '1h', '1d', '1w', '1M'].includes(config.initialEntryLevel)) {
        errors.push('Initial Entry Level must be a valid timeframe');
      }
      if (!config.vidyaLength || config.vidyaLength <= 0 || config.vidyaLength > 50) {
        errors.push('VIDYA Length must be between 1 and 50');
      }
      if (!config.vidyaMomentum || config.vidyaMomentum <= 0 || config.vidyaMomentum > 50) {
        errors.push('VIDYA Momentum must be between 1 and 50');
      }
      if (!config.bandDistance || config.bandDistance <= 0 || config.bandDistance > 10) {
        errors.push('Band Distance must be between 0.1 and 10.0');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }, []);
  
  // Get asset-specific configuration
  const assetConfig = getAssetAIConfig(symbol);
  const assetStrategies = getAssetStrategies(symbol);
  
  
  // Load existing configuration from Firestore when popup opens
  React.useEffect(() => {
    if (isOpen && selectedStrategy) {
      // 🔥 CRITICAL FIX: Load configuration from assetConfig
      let savedConfig = null;
      
      // Check per-asset structure (most reliable source)
      if (assetConfig?.activeStrategyInstance?.config) {
        savedConfig = assetConfig.activeStrategyInstance.config;
      }
      
      if (savedConfig) {
        setCurrentStrategyConfig(savedConfig);
      }
    }
  }, [isOpen, selectedStrategy, symbol, assetConfig]);
  
  // Check if AI is currently running for this asset
  const isAIRunningForAsset = React.useMemo(() => {
    const isAssetEnabled = assetConfig?.enabled || false;
    const isStrategyActive = assetConfig?.activeStrategyInstance?.isActive || false;
    const isTradeEnabled = assetConfig?.activeStrategyInstance?.aiTradeStartEnabled || false;
    const hasStrategy = !!assetConfig?.activeStrategyInstance;
    
    const result = isAssetEnabled && isStrategyActive && isTradeEnabled && hasStrategy;
    return result;
  }, [symbol, assetConfig]);
  
  // Debug logging for per-asset strategy selection
  React.useEffect(() => {
    if (isOpen) {
      // Settings opened - no logging needed
    }
  }, [isOpen, symbol, selectedStrategy]); // Removed problematic dependencies that cause infinite loops

  // The selectedStrategy prop is already passed from the parent component
  // No need for local state management - the parent handles the strategy selection

  // Memoize strategies to prevent unnecessary re-renders
  const localStrategies: Strategy[] = useMemo(() => {
    // 🔥 CRITICAL FIX: Use currentStrategyConfig state directly
    const savedConfig = currentStrategyConfig;
    
    // console.log(`🔧 Creating strategies for ${symbol} with config:`, {
    //   selectedStrategy,
    //   savedConfig,
    //   hasConfig: !!savedConfig,
    //   configDetails: savedConfig ? {
    //     quantity: savedConfig.quantity,
    //     profitThreshold: savedConfig.profitThreshold,
    //     lossThreshold: savedConfig.lossThreshold,
    //     period: savedConfig.period,
    //     multiplier: savedConfig.multiplier,
    //     tradeAmountPercent: savedConfig.tradeAmountPercent
    //   } : null
    // });
    
    // console.log(`🔧 Creating strategies for ${symbol} with config:`, {
    //   selectedStrategy,
    //   savedConfig,
    //   hasConfig: !!savedConfig,
    //   configDetails: savedConfig ? {
    //     quantity: savedConfig.quantity,
    //     profitThreshold: savedConfig.profitThreshold,
    //     lossThreshold: savedConfig.lossThreshold,
    //     period: savedConfig.period,
    //     multiplier: savedConfig.multiplier,
    //     tradeAmountPercent: savedConfig.tradeAmountPercent
    //   } : null
    // });
    
    return [
      {
        id: 'supertrend',
        name: 'HUGE♥︎HUG',
        description: 'VidyaHeart multi-timeframe strategy with contrarian entry logic based on VIDYA and ATR bands',
        component: <SuperTrendStrategy symbol={symbol} currentPrice={currentPrice} onConfigUpdate={handleConfigUpdate} onSignalUpdate={onSignalUpdate} isRunning={isRunning} initialConfig={selectedStrategy === 'supertrend' ? savedConfig : undefined} />,
        isActive: true
      }
    ];
  }, [symbol, currentPrice, handleConfigUpdate, onSignalUpdate, isRunning, selectedStrategy]);

  const currentStrategy = localStrategies.find(s => s.id === selectedStrategy);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4">
      <div className="theme-bg-quaternary rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 w-full max-w-[95vw] sm:w-[800px] sm:max-w-[90vw] h-[90vh] sm:h-[74vh] overflow-hidden">
        <div className="py-4 relative h-full flex flex-col">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200 z-10"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 border-b theme-border mb-2 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold theme-text-primary flex items-center">
              <Brain className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[#2AB7A6]" />
              AI Trading Settings
            </h3>
            <div className="text-xs sm:text-sm theme-text-quaternary mt-1">Asset: {symbol}</div>
          </div>

          {/* Main Content - Responsive Layout */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden px-2 sm:px-4">
            {/* Left Column - Strategy Selection */}
            <div className="w-full lg:w-1/2 lg:border-r theme-border lg:pr-4 theme-bg-primary mb-4 lg:mb-0">
              <div className="h-48 lg:h-full overflow-y-auto">
                <div className="px-2 sm:px-4 py-4">
                  <div className="space-y-2 sm:space-y-3">
                    {localStrategies.map((strategy) => (
                      <button
                        key={strategy.id}
                        onClick={() => onStrategyChange(strategy.id)}
                        className={`w-full p-3 sm:p-4 rounded-lg border transition-colors duration-200 text-left ${
                          selectedStrategy === strategy.id
                            ? 'border-[#2AB7A6] bg-[#2AB7A6]/10'
                            : 'theme-border hover:theme-border-secondary theme-bg-secondary'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium theme-text-primary text-xs sm:text-sm">{strategy.name}</div>
                            <div className="text-xs theme-text-quaternary mt-1 leading-relaxed hidden sm:block">{strategy.description}</div>
                          </div>
                          <div className="flex items-center space-x-2 ml-3">
                            {strategy.id === selectedStrategy && (
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            )}
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              selectedStrategy === strategy.id 
                                ? 'border-[#2AB7A6] bg-[#2AB7A6]' 
                                : 'theme-border'
                            }`}></div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Strategy Configuration */}
            <div className="w-full lg:w-1/2 lg:pl-4 theme-bg-secondary">
              <div className="h-64 lg:h-full overflow-y-auto">
                {currentStrategy ? (
                  <div className="px-2 sm:px-4 py-4">
                    <h3 className="text-xs font-medium theme-text-primary mb-4">
                      {currentStrategy.name} Configuration
                    </h3>
                    {currentStrategy.component}
                  </div>
                ) : (
                  <div className="px-2 sm:px-4 py-4 flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="text-xs theme-text-quaternary">Select a strategy to configure</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer with Save Strategy, Remove Strategy and Cancel Buttons */}
          <div className="px-6 py-4 border-t theme-border">
            <div className="flex justify-between">
              {/* Left side buttons - Context-aware based on strategy selection */}
              <div className="flex space-x-2">
                {/* Save Strategy Button - Only show when strategy is selected in UI */}
                {selectedStrategy && (
                  <button
                    onClick={async () => {
                      try {
                        // 🔥 CRITICAL FIX: Validate configuration before saving
                        const userConfig = currentStrategyConfig || assetConfig?.activeStrategyInstance?.config;
                        
                        if (!userConfig) {
                          setValidationMessage('No configuration found. Please configure the strategy first.');
                          setShowValidationError(true);
                          return;
                        }
                        
                        const validation = validateStrategyConfig(userConfig, selectedStrategy);
                        
                        if (!validation.isValid) {
                          setValidationMessage(`Please fix the following errors:\n\n${validation.errors.join('\n')}`);
                          setShowValidationError(true);
                          return;
                        }
                        
                        // console.log(`✅ Validation passed for ${selectedStrategy} strategy`);
                        
                        // Create strategy configuration using dynamic user input
                        // Load values from Firestore instead of hardcoded values
                        const getDynamicConfig = () => {
                          // Get saved config from Firestore
                          const savedConfig = currentStrategyConfig || assetConfig?.activeStrategyInstance?.config;
                          
                          return {
                            name: 'HUGE♥︎HUG',
                            description: 'VidyaHeart multi-timeframe strategy with contrarian entry logic',
                            // VidyaHeart Strategy Configuration
                            initialEntryLevel: savedConfig?.initialEntryLevel || '1m',
                            vidyaLength: savedConfig?.vidyaLength || 14,
                            vidyaMomentum: savedConfig?.vidyaMomentum || 20,
                            bandDistance: savedConfig?.bandDistance || 3.0,
                            // Use saved values from Firestore, or fallback to defaults
                            quantity: savedConfig?.quantity || 0.5,
                            tradeAmountPercent: savedConfig?.tradeAmountPercent || 100,
                            isActive: true,
                            // Profit/loss fields
                            profitThreshold: savedConfig?.profitThreshold || 1.99,
                            lossThreshold: savedConfig?.lossThreshold || 1.99,
                            levelOffset: savedConfig?.levelOffset || 1,
                            showSignals: savedConfig?.showSignals ?? true,
                            showStrategyPoints: savedConfig?.showStrategyPoints ?? false,
                            showStrategyAI: savedConfig?.showStrategyAI ?? true
                          };
                        };
                        
                        const strategyConfigs = {
                          supertrend: {
                            name: 'HUGE♥︎HUG',
                            description: 'VidyaHeart multi-timeframe strategy with contrarian entry logic',
                            defaultConfig: getDynamicConfig()
                          }
                        };

                        const config = strategyConfigs[selectedStrategy as keyof typeof strategyConfigs];
                        if (config) {
                          // Use dynamic configuration from user input, fallback to defaults
                          const userConfig = currentStrategyConfig || config.defaultConfig;
                          
                          // Debug logging for strategy configuration
                          // console.log(`💾 Saving strategy for ${symbol}:`, {
                          //   selectedStrategy,
                          //   userConfig,
                          //   quantity: userConfig.quantity,
                          //   profitThreshold: userConfig.profitThreshold,
                          //   lossThreshold: userConfig.lossThreshold
                          // });
                          
                          // Create strategy ID
                          const strategyId = `strategy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                          
                          const strategyConfig = {
                            id: strategyId,
                            name: config.name,
                            type: selectedStrategy as any,
                            symbol: symbol,
                            isActive: false, // Strategy is selected but NOT started yet
                            aiTradeStartEnabled: true, // Strategy is selected and ready to start
                            aiSelectedStrategy: config.name, // Save the selected strategy name
                            config: { ...userConfig }, // Use dynamic user configuration
                            parameters: { ...userConfig }, // Add parameters field for per-asset system compatibility
                            performance: {
                              totalTrades: 0,
                              winRate: 0,
                              totalPnL: 0,
                              sharpeRatio: 0,
                              maxDrawdown: 0
                            },
                            riskSettings: {
                              maxPositions: 1,
                              maxRiskPerTrade: 2,
                              stopLossPercent: 5,
                              takeProfitPercent: 10
                            },
                            createdAt: new Date().toISOString(),
                            lastModified: new Date().toISOString(),
                            enabled: true
                          };

                          // Save using NEW per-asset system
                          await saveAssetStrategy(symbol, strategyConfig);
                          
                          // Update UI to show strategy is selected
                          onStrategyChange(selectedStrategy);
                          
                          onClose();
                        }
                      } catch (error) {
                        console.error('Error saving strategy:', error);
                        // Still update the UI even if some operations fail
                        onStrategyChange(selectedStrategy);
                        onClose();
                      }
                    }}
                    className="px-4 py-1.5 bg-[#2AB7A6] hover:bg-[#2AB7A6]/90 text-white text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1.5"
                  >
                    <Save className="h-3 w-3" />
                    <span>Save Strategy</span>
                  </button>
                )}
                
                {/* 🔥 NEW: Test Thresholds Button - Only show when strategy is selected */}
                {selectedStrategy && (
                  <button
                    onClick={handleTestThresholds}
                    className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1.5"
                    title="Test current thresholds for this symbol"
                  >
                    <TestTube className="h-3 w-3" />
                    <span>Test Thresholds</span>
                  </button>
                )}
                
                {/* Start AI Trading Button - Only show when there's a saved strategy but AI is not running */}
                {assetConfig?.activeStrategyInstance && !isAIRunningForAsset && (
                  <button
                    onClick={async () => {
                      try {
                        console.log(`🚀 Starting AI Trading for ${symbol}...`);
                        await startAssetAITrading(symbol);
                        console.log(`✅ AI Trading started for ${symbol}`);
                        onClose();
                      } catch (error) {
                        console.error('Error starting AI trading:', error);
                      }
                    }}
                    className="px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1.5"
                  >
                    <Play className="h-3 w-3" />
                    <span>Start AI Trading</span>
                  </button>
                )}
                
                {/* Stop AI Trading Button - Only show when AI is running */}
                {isAIRunningForAsset && (
                  <button
                    onClick={async () => {
                      try {
                        console.log(`⏸️ Stopping AI Trading for ${symbol}...`);
                        await stopAssetAITrading(symbol);
                        console.log(`✅ AI Trading stopped for ${symbol}`);
                        onClose();
                      } catch (error) {
                        console.error('Error stopping AI trading:', error);
                      }
                    }}
                    className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1.5"
                  >
                    <Pause className="h-3 w-3" />
                    <span>Stop AI Trading</span>
                  </button>
                )}
                
                {/* Remove Strategy Button - Only show when there's a saved strategy (not just selected) */}
                {(assetConfig?.activeStrategyInstance || assetStrategies.length > 0 || selectedStrategy) && (
                  <button
                    onClick={async () => {
                      try {
                        // Call the parent's strategy change handler with empty string to remove strategy
                        onStrategyChange('');
                        
                        onClose();
                      } catch (error) {
                        console.error('Error removing strategy:', error);
                        // Still update the UI even if some operations fail
                        onStrategyChange('');
                        onClose();
                      }
                    }}
                    className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1.5"
                  >
                    <X className="h-3 w-3" />
                    <span>Remove Strategy</span>
                  </button>
                )}
              </div>
              
              {/* Cancel Button - Right */}
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded transition-colors duration-200"
              >
                <span>Cancel</span>
              </button>
            </div>
          </div>

        </div>
      </div>
      
      {/* Validation Error Popup */}
      {showValidationError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Invalid Configuration
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {validationMessage}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowValidationError(false)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors duration-200"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default memo(AITradingSettings);
